import type { Node as ProsemirrorNode } from 'prosemirror-model';
import type { Command, EditorState, Transaction } from 'prosemirror-state';
import { NodeSelection } from 'prosemirror-state';

export type AlignValue = 'left' | 'center' | 'right';
export type AlignAttrs = {
  align: AlignValue;
};

const applyToNode = (state: EditorState, align: AlignValue) => {
  const { tr, selection } = state;
  return tr.setNodeAttribute(selection.from, 'align', align);
};

const applyToText = (state: EditorState, align: AlignValue) => {
  const { doc, tr, selection } = state;

  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (node.attrs?.align) {
      tr.setNodeAttribute(pos, 'align', align);
    }
  });

  return tr;
};

export class AlignExtension {
  public static readonly validAlignValues: AlignValue[] = ['left', 'center', 'right'];

  public static readonly attrs = {
    align: { default: 'left' },
  };

  public static apply(align: AlignValue): Command {
    return function (state, dispatch): boolean {
      let transaction: Transaction;

      if (state.selection instanceof NodeSelection) {
        transaction = applyToNode(state, align);
      } else {
        transaction = applyToText(state, align);
      }

      if (!transaction) return false;
      if (dispatch) dispatch(transaction);
      return true;
    };
  }

  public static has(align: AlignValue) {
    return function (state: EditorState) {
      const { doc, selection } = state;
      const { from, to } = selection;

      if (selection instanceof NodeSelection) {
        return doc.nodeAt(state.selection.from)?.attrs.align === align;
      }

      if (from === 0 && to === 0) {
        return false;
      }

      let allNodesHaveAlign = true;
      doc.nodesBetween(from, to, node => {
        if (node.attrs?.align) {
          allNodesHaveAlign = allNodesHaveAlign && node.attrs?.align === align;
        }
      });
      return allNodesHaveAlign;
    };
  }

  public static getAlignAtPosition(state: EditorState, position: number): AlignValue | undefined {
    const { doc } = state;

    const val = doc.nodeAt(position)?.attrs.align;
    return val || undefined;
  }

  public parseDOMGetAttrs(node: HTMLElement, fallbackStyleAttr?: keyof CSSStyleDeclaration) {
    const { textAlign } = node.style || {};
    const fallbackVal = node.style && fallbackStyleAttr ? node.style[fallbackStyleAttr] : '';

    const validateFallbackVal = (val: any): val is AlignValue =>
      AlignExtension.validAlignValues.includes(<AlignValue>val);

    return <AlignAttrs>{
      align:
        textAlign ||
        (validateFallbackVal(fallbackVal) ? fallbackVal : '') ||
        AlignExtension.attrs.align.default,
    };
  }

  public getStyleSerialization(node: ProsemirrorNode) {
    const { align } = <AlignAttrs>node.attrs;
    if (align && align !== AlignExtension.attrs.align.default) {
      return `text-align:${align};`;
    }
    return null;
  }
}
